import React, {createContext, useState} from "react";
import {useLazyQuery, useMutation} from "@apollo/client";

import {
  LOGIN_MUTATION,
  REGISTER_MUTATION,
} from "../../graphql/mutations/auth.mutation";

import {JWT_TOKEN} from "../../constants/appConfig";
import {GET_PAGINATED_CART_ITEM_QUERY} from "../../graphql/queries/common.query";
import {
  ADD_SERVICE_TO_CART_MUTATION,
  CREATE_MY_ORDER_MUTATION,
  REMOVE_SERVICE_FROM_CART_MUTATION,
} from "../../graphql/mutations/services.mutation";
import {useHistory} from "react-router";
import toast from "react-hot-toast";
import {
  clearLocalStorage,
  getLocalStorage,
  setLocalStorage,
} from "../../lib/utils/storage.utils";

export interface IUser {
  id: number;
  email: string;
  role: string;
  headquarter: string;
  iat: number;
  firstName: string;
  lastName: string;
  agencyId: number;
}

export interface IAuthContext {
  user: IUser;
  setUser: any;
  loginMutation: any;
  registerMutation: any;
  logout: any;
  isAdmin: any;
  isLoggedIn: any;
  cartList: any;
  setCartList: any;
  addToCart: any;
  validateCartItems: any;
  addServiceToCart: any;
  removeServiceFromCart: any;
  refetchCartItems: any;
  createMyOrders: any;
  cartPopOver: any;
  setShowPopover: any;
}

const AuthContext = createContext<Partial<IAuthContext>>({});

const AuthProvider = (props: any) => {
  const history = useHistory();
  const [user, setUser] = useState(
    getLocalStorage("COS_MILANO_WEB_USER") || {}
  );
  const [cartList, setCartList] = useState<any[]>(
    getLocalStorage("cos-milano-web-cart") || []
  );

  const [getPaginatedCartItemQuery] = useLazyQuery(
    GET_PAGINATED_CART_ITEM_QUERY,
    {
      fetchPolicy: "network-only",
      onCompleted(data) {
        console.log("i got in here now");
        if (data?.getPaginatedCartItem?.data?.length > 0) {
          const tempCartList = data?.getPaginatedCartItem?.data;
          setCartList(
            tempCartList.map((item: any) => {
              return {
                cartId: item.id,
                serviceID: item?.service?.id,
                serviceName: item?.service?.name,
              };
            })
          );
        } else {
          setCartList(getLocalStorage("cos-milano-web-cart") || []);
        }
      },
    }
  );

  const [loginMutation] = useMutation(LOGIN_MUTATION, {
    onError(error) {
      if (error.message) {
        // ErrorMessage(error.message);
      }
    },
  });

  const [addServiceToCartMutation] = useMutation(ADD_SERVICE_TO_CART_MUTATION, {
    onError(error) {
      if (error.message) {
        // ErrorMessage(error.message);
      }
    },
    onCompleted() {
      getPaginatedCartItemQuery();
    },
  });

  const [removeServiceFromCartMutation] = useMutation(
    REMOVE_SERVICE_FROM_CART_MUTATION,
    {
      onCompleted() {
        console.log("i removed item from cart");
        getPaginatedCartItemQuery();
      },
    }
  );

  const [registerMutation] = useMutation(REGISTER_MUTATION);

  const [createMyOrders] = useMutation(CREATE_MY_ORDER_MUTATION);

  //
  const addServiceToCart = (serviceId: number) => {
    addServiceToCartMutation({
      variables: {serviceId},
    });
  };

  //
  const addToCart = (
    serviceID: number,
    serviceName: string,
    cartID: string
  ) => {
    if (isLoggedIn()) {
      addServiceToCart(serviceID);
    } else {
      const updatedCartList = [
        ...(getLocalStorage("cos-milano-web-cart") ?? []),
        {
          serviceID,
          serviceName,
          cartID,
        },
      ];
      setLocalStorage("cos-milano-web-cart", updatedCartList);
      setCartList(updatedCartList);
    }
  };

  //
  const removeServiceFromCart = (serviceId: number) => {
    removeServiceFromCartMutation({
      variables: {id: serviceId},
    });
  };

  const validateCartItems = () => {
    if (isLoggedIn()) {
      // handleCreateMyOrder();
      history.push("/personale/add-to-cart");
    } else {
      history.push("/login", {
        to: "/personale",
        action: "updateCart",
      });
    }
  };
  const logout = () => {
    clearLocalStorage("cos-milano-web-token");
    clearLocalStorage("COS_MILANO_WEB_USER");
    clearLocalStorage("cos-milano-web-cart");
    setCartList([]);
    toast.success("Logout avvenuto con successo");
    history.push("/");
  };

  const isLoggedIn = () => {
    const getToken = getLocalStorage(JWT_TOKEN);
    return !!getToken;
  };

  const [cartPopOver, setShowPopover] = React.useState({
    showPopover: false,
    event: undefined,
  });

  React.useEffect(() => {
    isLoggedIn() && getPaginatedCartItemQuery();
  }, []);

  return (
    <AuthContext.Provider
      {...props}
      value={{
        loginMutation,
        logout,
        registerMutation,
        user,
        isLoggedIn,
        setUser,
        cartList,
        setCartList,
        addToCart,
        validateCartItems,
        addServiceToCart,
        removeServiceFromCart,
        refetchCartItems: getPaginatedCartItemQuery,
        createMyOrders,
        cartPopOver,
        setShowPopover,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export {AuthProvider, AuthContext};
